import React, { useState } from 'react';
import Classes from './Hero.module.css';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';

import { motion } from 'framer-motion';
import { ArrowNextIcon, ArrowPrevIcon } from '../../../../../utils/Icons';

/* ─────────  blur reveal variant ───────── */
const blurReveal = (delay = 0, y = 22, blur = 10) => ({
  initial: { y, opacity: 0, filter: `blur(${blur}px)` },
  animate: { y: 0, opacity: 1, filter: 'blur(0px)' },
  transition: { duration: 0.5, ease: 'easeOut', delay },
});

const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: 'easeOut' } },
};

/* ─────────  dummy data  ───────── */
const reviews = [
  {
    name: 'Maya Lopez',
    date: '3 days ago',
    rating: 5,
    text:
      "I booked my first delivery during lunch from your website and it couldn’t have been easier. The appointment flow was clean, and I liked that I could pick an exact time instead of a vague window.\n\nThe live tracking kept me posted from confirmed to completed. I walked out of my apartment in Spring Valley to a topped-off tank and didn’t spend a single minute hunting for a gas station."
  },
  {
    name: 'Jordan Miles',
    date: '1 week ago',
    rating: 5,
    text:
      "Had a tight morning in Summerlin and tried this for the first time. Scheduling took under two minutes and I immediately got an ETA and status.\n\nThe driver was right on time and professional. Honestly, not having to detour for gas on the 215 was the best part—this service fits perfectly into a busy Vegas schedule."
  },
  {
    name: 'Priya Patel',
    date: 'yesterday',
    rating: 5,
    text:
      "I had to reschedule twice because my meetings moved, and the appointment management made it painless. No back-and-forth calls—just tap, pick a new time, done.\n\nWhen the delivery started, I could track everything in real time. I came downstairs after a call and my car in Henderson was already full, receipts and status neatly logged in my account."
  },
  {
    name: 'Dylan Carter',
    date: '2 weeks ago',
    rating: 4,
    text:
      "Great experience overall. There was a small delay due to Strip traffic, but the live updates and revised ETA were accurate so I never felt left hanging.\n\nFueling was quick, clean, and the team double-checked my cap and driveway area before leaving. I’ll definitely use this again for weekend errands."
  },
  {
    name: 'Aisha Khan',
    date: '5 days ago',
    rating: 5,
    text:
      "The Vegas heat is no joke, and skipping the gas station after a double shift was priceless. I booked on the site, chose a late slot, and they arrived exactly when promised.\n\nI appreciated the clear pricing and the way everything is saved in my account history. It’s become part of my weekly routine now."
  },
  {
    name: 'Robert Chen',
    date: '1 month ago',
    rating: 5,
    text:
      "I run a pretty tight schedule on the east side and hate wasting time in lines. The booking was smooth, confirmation instant, and I liked the simple status bar that goes from scheduled to on the way to fueling.\n\nMy driveway stayed spotless—no smell, no mess—and I received a delivery summary right after. Five stars for reliability."
  },
  {
    name: 'Selena Brooks',
    date: '2 days ago',
    rating: 5,
    text:
      "We used this for our office parking lot near Downtown. I booked two cars back-to-back and coordinated during a team meeting.\n\nThey pinged me when they started and wrapped up exactly on the minute. Not one person had to leave the building for a gas run."
  },
  {
    name: 'Marcus Nguyen',
    date: '3 weeks ago',
    rating: 5,
    text:
      "Tried it before a family day trip and it genuinely removed the last-minute gas scramble. Appointment set the night before, and by breakfast the car was full.\n\nThe timeline and notifications felt like a rideshare—very familiar and easy to trust. Kids in the car, no detours, just go."
  },
  {
    name: 'Gabriela Ortiz',
    date: '4 days ago',
    rating: 5,
    text:
      "Clean, quick, and professional service in Enterprise. I was nervous about spills, but the tech had pads and gear and left my driveway exactly as he found it.\n\nThe summary in my account shows date, time, and location, which makes it easy to budget and track usage."
  },
  {
    name: 'Ethan Wright',
    date: '2 weeks ago',
    rating: 4,
    text:
      "Appreciated the reminders and ETA updates—helpful when you’re juggling errands. The site made it easy to choose a time that didn’t conflict with school pickup.\n\nOnly nitpick: I’d love a slightly tighter arrival window, but the tracking was accurate and fueling was fast."
  },
  {
    name: 'Nadia Ahmed',
    date: '6 days ago',
    rating: 5,
    text:
      "Booked a late-night delivery after a long hospital shift. The process was straightforward, and I didn’t have to stand under bright station lights at 1 AM.\n\nWhen I woke up, my car in North Las Vegas was full and a receipt was already in my inbox. Safest refuel I’ve had."
  },
  {
    name: 'Kevin O’Donnell',
    date: '1 week ago',
    rating: 5,
    text:
      "We tried this for our small business vans in Spring Valley. Coordinated three appointments and the routing was handled perfectly.\n\nThe dashboard made it simple to view each delivery, and we didn’t lose any time sending drivers out for fuel. Great for fleet efficiency."
  },
  {
    name: 'Sofia Rossi',
    date: '2 weeks ago',
    rating: 5,
    text:
      "Doorstep fueling felt like the future. I picked a time, added notes for my gated community, and they followed instructions to a tee.\n\nNo gas station lines, no detours off the 215, just a full tank and a clean driveway. I’m a convert."
  },
  {
    name: 'Andre Williams',
    date: '3 days ago',
    rating: 5,
    text:
      "Support proactively confirmed my apartment building details and gate code. That small step saved us both time on delivery day.\n\nThe real-time status updates were spot on, and fueling finished before my Zoom call ended. Seamless from start to finish."
  },
  {
    name: 'Hannah Lee',
    date: 'today',
    rating: 5,
    text:
      "From confirm to fill-up, the live tracker made everything stress-free. I could see when the driver was en route, on site, and wrapping up.\n\nBeing able to cancel or reschedule right in the portal is a game changer. Highly recommend for anyone in Las Vegas who hates gas station runs."
  }
];

/* ─────────  single review card ───────── */
function ReviewCard({ review }) {
  const [expanded, setExpanded] = useState(false);
  const needsClamp = review.text.length > 180;

  return (
    <div className={Classes.card}>
      <img src="/assets/icons/google.png" alt="Google" className={Classes.gIcon} />

      <div className={Classes.flexAlign10}>
        <img src="/assets/icons/star.png" alt="star" className={Classes.star} />
        <img src="/assets/icons/star.png" alt="star" className={Classes.star} />
        <img src="/assets/icons/star.png" alt="star" className={Classes.star} />
        <img src="/assets/icons/star.png" alt="star" className={Classes.star} />
        <img src="/assets/icons/star.png" alt="star" className={Classes.star} />
      </div>

      <p className={`${Classes.text} ${expanded ? Classes.expanded : ''}`}>
        {review.text}
      </p>

      {needsClamp && (
        <button onClick={() => setExpanded(!expanded)} className={Classes.toggle}>
          {expanded ? 'Read Less' : 'Read More'}
        </button>
      )}

      <h4 className={Classes.name}>{review.name}</h4>
      <p className={Classes.date}>{review.date}</p>
    </div>
  );
}

/* ─────────  main section ───────── */
export default function Hero() {
  return (
    <section className={Classes.wrapper} id="testimonials">
      {/* <motion.div className={Classes.miniText} variants={fadeUp} >Testimonials</motion.div> */}
      <motion.h1 className={Classes.subheading} variants={fadeUp}>
        Here’s What Our Customers Are Saying
      </motion.h1>

      <motion.div variants={fadeUp} className={Classes.ratingRow}>
        <div className={Classes.flexAlign10}>
          <img src="/assets/icons/star.png" alt="star" className={Classes.star1} />
          <img src="/assets/icons/star.png" alt="star" className={Classes.star1} />
          <img src="/assets/icons/star.png" alt="star" className={Classes.star1} />
          <img src="/assets/icons/star.png" alt="star" className={Classes.star1} />
          <img src="/assets/icons/star.png" alt="star" className={Classes.star1} />
        </div>
        <motion.p className={Classes.text} >Rated 5.0 by Hundreds of Reviews</motion.p>
      </motion.div>

      {/* Swiper carousel */}
      <Swiper
        className={Classes.swiper}
        modules={[Navigation, Autoplay]}
        navigation={{
          nextEl: `.${Classes.next}`,
          prevEl: `.${Classes.prev}`,
        }}
        loop
        autoplay={{ delay: 4500, disableOnInteraction: false }}
        spaceBetween={24}
        slidesPerView={1}
        breakpoints={{
          640: { slidesPerView: 1 },
          768: { slidesPerView: 2 },
          1024: { slidesPerView: 3 },
        }}
      >
        {reviews.map((r, i) => (
          <SwiperSlide key={i}>
            {/* 👇 blur ➜ reveal per slide */}
            <motion.div
              variants={blurReveal(i * 0.05)}
              initial="initial"
              whileInView="animate"
            // viewport={{ once: false, amount: 0.6 }} // animate again when it re-enters
            >
              <ReviewCard review={r} />
            </motion.div>
          </SwiperSlide>
        ))}

        {/* navigation arrows */}
        <div className={Classes.prev}>
          <ArrowPrevIcon height="22px" fill="black" />
        </div>
        <div className={Classes.next}>
          <ArrowNextIcon height="22px" fill="black" />
        </div>
      </Swiper>

      {/* <a
                target="_blank"
                href="https://www.google.com/search?sca_esv=12c7b01f52a29939&sxsrf=AE3TifMUFCWjfQxuIYMqqCmGireykR3fMQ:1752767565346&si=AMgyJEtREmoPL4P1I5IDCfuA8gybfVI2d5Uj7QMwYCZHKDZ-ExG0NIqoYo2NWzU-08o-xiSWtqxhYhCXovzbZYkSkjmSAQSoDbGAVpjJZiz0ELfsP0fsk_MAyT6jGIlb8ANNLiyqdXey&q=561-Fix-Home+Reviews&sa=X&ved=2ahUKEwiYz-yAoMSOAxUwQjABHfgKIawQ0bkNegQIKxAE&cshid=1752767620192025&biw=1717&bih=1294&dpr=1"
            >
                <button className={Classes.viewAll}>View All Reviews</button>
            </a> */}
    </section>
  );
}
